import React from "react";
import { useSearchParams } from "react-router-dom";
import type {
  ProductListBlock,
  ItemCard,
  ProductCardBlock,
} from "../../../types/content";
import { ProductCard } from "../ProductCard";
import { itemCardToProductCard } from "../ProductCard/itemCardToProductCard";
import { ProductListingTracker } from "@/components/analytics/gtm-trackers";
import { ChevronDownIcon } from "../../icons";
import { resolveHref } from "@/utils/url";
import { DEVICE_FILTER_PARAM, filterByDevice } from "@/utils/deviceFilter";
import { getLocale } from "@/utils/locale-store";
import { fillTemplate } from "@/utils/text";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./ProductListSection.module.css";

export interface ProductListSectionProps {
  block: ProductListBlock;
  className?: string;
}

/** Products shown per page in catalogue mode when the block does not say. */
const DEFAULT_PAGE_SIZE = 12;

const SORT_IDS = [
  "featured",
  "priceAsc",
  "priceDesc",
  "nameAsc",
  "newest",
] as const;

type SortId = (typeof SORT_IDS)[number];

/** Dictionary key and English fallback for each option in the sort control. */
const SORT_LABELS: Record<SortId, { key: string; fallback: string }> = {
  featured: { key: "Tracker.Products.Sort.Featured", fallback: "Featured" },
  priceAsc: {
    key: "Tracker.Products.Sort.PriceAsc",
    fallback: "Price — low to high",
  },
  priceDesc: {
    key: "Tracker.Products.Sort.PriceDesc",
    fallback: "Price — high to low",
  },
  nameAsc: { key: "Tracker.Products.Sort.NameAsc", fallback: "Name — A to Z" },
  newest: { key: "Tracker.Products.Sort.Newest", fallback: "Newest first" },
};

/**
 * An unset locale would make `Intl.Collator` throw, which must never take the
 * listing down — fall back to the host's default collation.
 */
function createNameCollator(locale: string): Intl.Collator {
  try {
    return new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  } catch {
    return new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
  }
}

/**
 * Orders products for the sort control. Products missing the field being sorted
 * on keep their CMS order at the end of the list rather than jumping to the top.
 * `Array.prototype.sort` is stable, so equal keys stay in the order the CMS gave.
 */
function sortProducts(
  items: ItemCard[],
  sortId: SortId,
  locale: string
): ItemCard[] {
  if (sortId === "featured") return items;

  const withFallbackLast = (
    compare: (a: ItemCard, b: ItemCard) => number,
    has: (item: ItemCard) => boolean
  ) => {
    return [...items].sort((a, b) => {
      const aHas = has(a);
      const bHas = has(b);
      if (!aHas && !bHas) return 0;
      if (!aHas) return 1;
      if (!bHas) return -1;
      return compare(a, b);
    });
  };

  const priceOf = (item: ItemCard) => item.price?.calculatedPrice ?? null;
  const hasPrice = (item: ItemCard) => typeof priceOf(item) === "number";

  switch (sortId) {
    case "priceAsc":
      return withFallbackLast((a, b) => priceOf(a)! - priceOf(b)!, hasPrice);
    case "priceDesc":
      return withFallbackLast((a, b) => priceOf(b)! - priceOf(a)!, hasPrice);
    case "nameAsc": {
      const collator = createNameCollator(locale);
      return withFallbackLast(
        (a, b) => collator.compare(a.header, b.header),
        (item) => Boolean(item.header)
      );
    }
    case "newest": {
      const timeOf = (item: ItemCard) => {
        if (!item.publishDate) return null;
        const time = new Date(item.publishDate).getTime();
        return Number.isNaN(time) ? null : time;
      };
      return withFallbackLast(
        (a, b) => timeOf(b)! - timeOf(a)!,
        (item) => timeOf(item) !== null
      );
    }
    default:
      return items;
  }
}

/**
 * Product List Section - displays a grid of product cards with optional heading.
 *
 * In `grid` mode (the default) it is the plain curated grid it has always been.
 * In `catalogue` mode — what a category page uses — it also renders the result
 * count, the sort control, paged "load more" and the footer progress line.
 */
export const ProductListSection: React.FC<ProductListSectionProps> = ({
  block,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const {
    heading,
    products: sourceProducts,
    columns,
    cardVariant = "minimal",
    showViewAllLink,
    viewAllLink,
    listingMode,
    pageSize: blockPageSize,
    totalCount,
  } = block;

  const isCatalogue = listingMode === "catalogue";
  const pageSize =
    blockPageSize && blockPageSize > 0 ? blockPageSize : DEFAULT_PAGE_SIZE;

  const [sortId, setSortId] = React.useState<SortId>("featured");
  const [visibleCount, setVisibleCount] = React.useState(pageSize);

  // `?device=artemis` narrows the listing to what fits that device — the link
  // the My Account "Compatible accessories" row sends members along (GROW-397).
  // Only the catalogue reads it: it is the category listing, while a curated
  // grid elsewhere on the page shows what its editor picked, whatever the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const deviceParam = isCatalogue
    ? searchParams.get(DEVICE_FILTER_PARAM)
    : null;
  const { items: listedProducts, deviceLabel } = React.useMemo(
    () => filterByDevice(sourceProducts ?? [], deviceParam),
    [sourceProducts, deviceParam]
  );
  const isDeviceFiltered = deviceLabel !== null;

  // A new order or a new filter is a new list, so paging starts again from the top.
  React.useEffect(() => {
    setVisibleCount(pageSize);
  }, [sortId, pageSize, deviceParam]);

  const locale = getLocale().language;

  const products: ProductCardBlock[] = React.useMemo(() => {
    const ordered = isCatalogue
      ? sortProducts(listedProducts, sortId, locale)
      : listedProducts;
    return ordered.map(itemCardToProductCard);
  }, [listedProducts, isCatalogue, sortId, locale]);

  // A filter that matches nothing still renders, so its notice — and the way
  // back to the whole category — stays on screen.
  if (products.length === 0 && !isDeviceFiltered) {
    return null;
  }

  const clearDeviceFilter = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(DEVICE_FILTER_PARAM);
      return next;
    });
  };

  const visibleProducts = isCatalogue
    ? products.slice(0, visibleCount)
    : products;
  const hasMore = isCatalogue && visibleProducts.length < products.length;

  /**
   * How many products the category holds. The backend truncates the payload at
   * `maxProducts`, so `totalCount` can exceed what was actually loaded — never
   * let it drop below the loaded count, or the footer would under-report.
   * Filtered by device, the backend's count is the whole category's, so only
   * the matches loaded can be counted.
   */
  const total = isDeviceFiltered
    ? products.length
    : Math.max(totalCount ?? 0, products.length);
  const allShown = visibleProducts.length >= total;

  const containerClasses = [
    styles.section,
    isCatalogue ? styles.catalogue : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  // The backend sends an empty string rather than omitting the property, so a
  // destructuring default would never apply.
  const gridClasses = [styles.grid, styles[`cols${columns || "3"}`]]
    .filter(Boolean)
    .join(" ");

  const productsLabel = t(
    localizedTexts,
    "Tracker.Products.Products",
    "Products"
  );

  // The result count bolds its number, so the template is split rather than
  // filled. A template without the token (translation-debug mode returns the
  // key) still renders the number, just without the surrounding copy.
  const resultCountTemplate = t(
    localizedTexts,
    "Tracker.Products.ResultCount",
    "{count} products"
  );
  const [resultCountBefore, resultCountAfter] =
    resultCountTemplate.split("{count}");

  const footerText = allShown
    ? fillTemplate(
        t(
          localizedTexts,
          "Tracker.Products.ShowingAll",
          "Showing all {total} products"
        ),
        { total: String(total) }
      )
    : fillTemplate(
        t(
          localizedTexts,
          "Tracker.Products.ShowingOf",
          "Showing {count} of {total} products"
        ),
        { count: String(visibleProducts.length), total: String(total) }
      );

  return (
    <section className={containerClasses} data-component="product-list-section">
      {listedProducts.length > 0 && (
        <ProductListingTracker products={listedProducts} />
      )}
      {/* Header with heading and optional view all link */}
      {(heading || (showViewAllLink && viewAllLink?.url)) && (
        <div className={styles.header}>
          {heading && <h2 className={styles.heading}>{heading}</h2>}
          {showViewAllLink && viewAllLink?.url && (
            <a
              href={resolveHref(viewAllLink, locale)}
              className={styles.viewAllLink}
              target={viewAllLink.target || "_self"}
              rel={
                viewAllLink.target === "_blank"
                  ? "noopener noreferrer"
                  : undefined
              }
            >
              {t(localizedTexts, "Tracker.Generic.ViewAll", "View all")}
              <svg
                className={styles.viewAllIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          )}
        </div>
      )}

      {/* Active device filter, with the way back to the whole category */}
      {isDeviceFiltered && (
        <div className={styles.deviceFilter}>
          <span className={styles.deviceFilterText}>
            {fillTemplate(
              t(
                localizedTexts,
                "Tracker.Products.FilteredByDevice",
                "Showing products for {device}"
              ),
              { device: deviceLabel }
            )}
          </span>
          <button
            type="button"
            className={styles.deviceFilterClear}
            onClick={clearDeviceFilter}
          >
            {t(
              localizedTexts,
              "Tracker.Products.ClearDeviceFilter",
              "Show all products"
            )}
          </button>
        </div>
      )}

      {/* Result count and sort control */}
      {isCatalogue && (
        <div className={styles.toolbar}>
          <p className={styles.resultCount}>
            {resultCountBefore}
            <strong className={styles.resultCountValue}>{total}</strong>
            {resultCountAfter ?? ""}
          </p>

          <div className={styles.sort}>
            <label className={styles.sortLabel} htmlFor="product-list-sort">
              {t(localizedTexts, "Tracker.Products.SortBy", "Sort by")}
            </label>
            <div className={styles.sortControl}>
              <select
                id="product-list-sort"
                className={styles.sortSelect}
                value={sortId}
                onChange={(event) => setSortId(event.target.value as SortId)}
              >
                {SORT_IDS.map((id) => (
                  <option key={id} value={id}>
                    {t(
                      localizedTexts,
                      SORT_LABELS[id].key,
                      SORT_LABELS[id].fallback
                    )}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                className={styles.sortChevron}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      )}

      {/* Product grid */}
      {products.length > 0 && (
        <div className={gridClasses} role="list" aria-label={productsLabel}>
          {visibleProducts.map((product, index) => (
            <div
              key={product.id || index}
              className={styles.gridItem}
              role="listitem"
            >
              <ProductCard block={product} variantOverride={cardVariant} />
            </div>
          ))}
        </div>
      )}

      {/* Footer count, progress and load more */}
      {isCatalogue && products.length > 0 && (
        <div className={styles.listingFooter}>
          <p className={styles.footerCount} aria-live="polite">
            {footerText}
          </p>

          <div
            className={styles.progress}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={visibleProducts.length}
            aria-label={productsLabel}
          >
            <div
              className={styles.progressFill}
              style={{
                width: `${total > 0 ? (visibleProducts.length / total) * 100 : 0}%`,
              }}
            />
          </div>

          {hasMore ? (
            <button
              type="button"
              className={styles.loadMore}
              onClick={() => setVisibleCount((count) => count + pageSize)}
            >
              {t(localizedTexts, "Tracker.Products.LoadMore", "Load more gear")}
            </button>
          ) : (
            <p className={styles.endOfResults}>
              {t(
                localizedTexts,
                "Tracker.Products.EndOfResults",
                "That's everything — try a category to narrow it down."
              )}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default ProductListSection;
